<script setup>
import { ref, computed, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

const emit = defineEmits(['close']);
const { t } = useI18n();
const store = useStore();

const query = ref('');
const inputRef = ref(null);
const currentIndex = ref(-1);

const currentChat = computed(() => store.getters.getSelectedChat);

const allMessages = computed(() => {
  const messages = currentChat.value?.messages || [];
  return messages.filter(
    m => m.content && typeof m.id === 'number' && m.message_type !== 2
  );
});

const results = computed(() => {
  const q = query.value.trim().toLowerCase();
  if (!q || q.length < 2) return [];
  return allMessages.value.filter(m => m.content.toLowerCase().includes(q));
});

const resultCount = computed(() => results.value.length);

const navigateTo = index => {
  if (results.value.length === 0) return;
  currentIndex.value = index;
  const msg = results.value[index];
  if (msg) {
    emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, { messageId: msg.id });
  }
};

const goNext = () => {
  if (resultCount.value === 0) return;
  const next =
    currentIndex.value < resultCount.value - 1 ? currentIndex.value + 1 : 0;
  navigateTo(next);
};

const goPrev = () => {
  if (resultCount.value === 0) return;
  const prev =
    currentIndex.value > 0 ? currentIndex.value - 1 : resultCount.value - 1;
  navigateTo(prev);
};

const close = () => {
  query.value = '';
  currentIndex.value = -1;
  emit('close');
};

const onKeydown = e => {
  if (e.key === 'Escape') {
    close();
  } else if (e.key === 'Enter') {
    if (e.shiftKey) {
      goPrev();
    } else {
      goNext();
    }
  }
};

watch(query, () => {
  currentIndex.value = -1;
  if (results.value.length > 0) {
    nextTick(() => navigateTo(0));
  }
});

watch(
  () => inputRef.value,
  el => {
    if (el) el.focus();
  }
);
</script>

<template>
  <div
    class="flex items-center gap-2 px-3 py-1 border-b border-n-weak bg-n-solid-2"
  >
    <span class="i-lucide-search text-n-slate-10 size-3.5 flex-shrink-0" />
    <input
      ref="inputRef"
      v-model="query"
      type="text"
      class="flex-1 h-5 text-xs bg-transparent border-0 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none"
      :placeholder="t('CONVERSATION.SEARCH.PLACEHOLDER')"
      @keydown="onKeydown"
    />
    <span
      v-if="query.length >= 2"
      class="text-xs text-n-slate-10 whitespace-nowrap"
    >
      {{
        resultCount > 0
          ? `${currentIndex + 1}/${resultCount}`
          : t('CONVERSATION.SEARCH.NO_RESULTS')
      }}
    </span>
    <button
      v-if="resultCount > 0"
      class="p-0.5 rounded hover:bg-n-alpha-2"
      @click="goPrev"
    >
      <span class="i-lucide-chevron-up size-4 text-n-slate-11" />
    </button>
    <button
      v-if="resultCount > 0"
      class="p-0.5 rounded hover:bg-n-alpha-2"
      @click="goNext"
    >
      <span class="i-lucide-chevron-down size-4 text-n-slate-11" />
    </button>
    <button class="p-0.5 rounded hover:bg-n-alpha-2" @click="close">
      <span class="i-lucide-x size-4 text-n-slate-11" />
    </button>
  </div>
</template>
